import { useState, useEffect } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Input, Button, Textarea,
} from '@heroui/react';
import SelectableChip from '@/components/common/SelectableChip';
import { useCreateCompany, useUpdateCompany } from '@/api/companies';
import { CompanyType } from '@estimator/shared';
import type { Company, CompanyTypeEnum } from '@estimator/shared';

interface CompanyFormProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Company;
}

const ALL_TYPES: CompanyTypeEnum[] = [
  CompanyType.CONTRACTOR, CompanyType.MANUFACTURER, CompanyType.ARCHITECT, CompanyType.DESIGNER,
  CompanyType.GENERAL_CONTRACTOR, CompanyType.SUBCONTRACTOR, CompanyType.SUPPLIER, CompanyType.OTHER,
];

export default function CompanyForm({ isOpen, onClose, company }: CompanyFormProps) {
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const isEdit = !!company;

  const [name, setName] = useState('');
  const [types, setTypes] = useState<CompanyTypeEnum[]>([]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (company) {
      setName(company.name);
      setTypes((company.types as CompanyTypeEnum[]) ?? []);
      setPhone(company.phone ?? '');
      setEmail(company.email ?? '');
      setWebsite(company.website ?? '');
      setAddress(company.address ?? '');
      setNotes(company.notes ?? '');
    } else {
      setName(''); setTypes([]); setPhone(''); setEmail('');
      setWebsite(''); setAddress(''); setNotes('');
    }
    setError(null);
  }, [company, isOpen]);

  const toggleType = (t: CompanyTypeEnum) => {
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        types,
        phone: phone || undefined,
        email: email || undefined,
        website: website || undefined,
        address: address || undefined,
        notes: notes || undefined,
      };
      if (isEdit) {
        await updateCompany.mutateAsync({ id: company!.id, ...payload });
      } else {
        await createCompany.mutateAsync(payload);
      }
      onClose();
    } catch (err: unknown) {
      setError((err as any)?.response?.data?.message ?? 'Something went wrong');
    }
  };

  const isPending = createCompany.isPending || updateCompany.isPending;
  const inputClassNames = {
    inputWrapper: 'border border-[var(--border)] bg-[var(--bg-panel)]',
    input: 'text-[var(--text-primary)]',
    label: 'text-[var(--text-secondary)]',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Company' : 'New Company'}</ModalHeader>
        <ModalBody className="flex flex-col gap-4">
          <Input label="Name" value={name} onValueChange={setName} isRequired variant="bordered" classNames={inputClassNames} />

          <div>
            <label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Types</label>
            <div className="flex gap-1.5 flex-wrap">
              {ALL_TYPES.map((t) => (
                <SelectableChip
                  key={t}
                  selected={types.includes(t)}
                  onToggle={() => toggleType(t)}
                >
                  {t.charAt(0) + t.slice(1).toLowerCase().replace('_', ' ')}
                </SelectableChip>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="Phone" value={phone} onValueChange={setPhone} variant="bordered" classNames={inputClassNames} />
            <Input label="Email" value={email} onValueChange={setEmail} variant="bordered" classNames={inputClassNames} />
          </div>

          <Input label="Website" value={website} onValueChange={setWebsite} variant="bordered" classNames={inputClassNames} />
          <Input label="Address" value={address} onValueChange={setAddress} variant="bordered" classNames={inputClassNames} />
          <Textarea label="Notes" value={notes} onValueChange={setNotes} variant="bordered" classNames={inputClassNames} />

          {error && <p className="text-sm text-red-600">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>Cancel</Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isPending} isDisabled={!name.trim()}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
